import { useQuery } from "@tanstack/react-query";
import { useSubscription } from "./use-billing";
import { useWorkspace } from "./use-workspace";
import { hasActivePlan } from "@/lib/api/billing";
import {
  countCustomers,
  countFinalisedInvoicesThisMonth,
  countQuotesThisMonth,
} from "@/lib/api/usage";
import { useAuth } from "@/lib/auth/auth-context";
import { normalizePlan, PLAN_LIMITS } from "@/lib/plans/plans";

/** The current workspace's plan (its owner's subscription) and what it includes. */
export function usePlan() {
  const workspace = useWorkspace();
  const plan = normalizePlan(workspace.data?.plan);
  return { plan, limits: PLAN_LIMITS[plan], isLoading: workspace.isLoading };
}

/** The signed-in user's own plan: creating workspaces depends on it, not on the current one. */
export function useOwnPlan() {
  const { user } = useAuth();
  const subscription = useSubscription(user?.id);
  const plan = hasActivePlan(subscription.data) ? normalizePlan(subscription.data?.plan) : "free";
  return { plan, limits: PLAN_LIMITS[plan], isLoading: subscription.isLoading };
}

// Under the customers / invoices keys, so their mutations refresh the counts too.
export function useCustomerCount(enabled = true) {
  return useQuery({ queryKey: ["customers", "count"], queryFn: countCustomers, enabled });
}

export function useInvoiceUsage(enabled = true) {
  return useQuery({
    queryKey: ["invoices", "usage"],
    queryFn: () => countFinalisedInvoicesThisMonth(),
    enabled,
  });
}

export function useQuoteUsage(enabled = true) {
  return useQuery({
    queryKey: ["invoices", "quote-usage"],
    queryFn: () => countQuotesThisMonth(),
    enabled,
  });
}
